import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, BookOpen, FileDown, FileText, Microscope, Sparkles } from "lucide-react";
import Navigation from "../components/Navigation";
import DraftPreview from "../components/DraftPreview";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAPER_TYPE_LABELS = {
  literature_review: "Literature review",
  journal_article: "Journal article",
  thesis_chapter: "Thesis chapter",
  technical_report: "Technical report",
};

export default function SharedDraftPage({ user }) {
  const { token } = useParams();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/research/shared/${token}`);
        setDraft(resp.data);
      } catch (err) {
        setError(
          err.response?.status === 404
            ? "This share link is invalid or has been revoked."
            : "Could not load this shared draft. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f6f2]">
        <Navigation user={user} />
        <div className="text-center py-24 text-slate-500">Loading shared draft…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f6f2]">
        <Navigation user={user} />
        <main className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="shared-draft-error">
          <div className="w-14 h-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-950">{error}</h1>
          <Button asChild className="mt-7 bg-slate-900 hover:bg-slate-800"><Link to="/">Go to The Archive</Link></Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      <Navigation user={user} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="shared-draft-page">
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-5 py-4 flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 text-sm text-orange-900">
            <Microscope className="w-5 h-5 shrink-0" />
            <div>
              <p className="font-semibold">Shared research draft · read-only</p>
              <p className="text-orange-700 text-xs mt-0.5">
                {PAPER_TYPE_LABELS[draft.paper_type] || "Research draft"} · {draft.language} · {draft.citation_style}
                {draft.created_at ? ` · ${new Date(draft.created_at).toLocaleDateString()}` : ""}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline" data-testid="shared-draft-docx-btn">
              <a href={`${API}/research/shared/${token}/docx`}>
                <FileText className="w-4 h-4 mr-1.5" />Word
              </a>
            </Button>
            <Button asChild size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="shared-draft-pdf-btn">
              <a href={`${API}/research/shared/${token}/pdf`}>
                <FileDown className="w-4 h-4 mr-1.5" />Download PDF
              </a>
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-9">
          <DraftPreview markdown={draft.draft_markdown} />
        </div>

        {draft.sources?.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mt-6">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen className="w-5 h-5 text-orange-600" />
              <h2 className="text-2xl font-semibold text-slate-950">Library sources</h2>
            </div>
            <div className="space-y-3">
              {draft.sources.map((source) => (
                <article key={source.ref_id} id={`source-${source.ref_id}`} className="rounded-lg border border-slate-200 p-4 scroll-mt-24">
                  <div className="flex gap-3">
                    <span className={`shrink-0 rounded text-white text-xs font-semibold px-2 py-1 h-fit ${source.source_kind === "open_access" ? "bg-sky-700" : "bg-slate-900"}`}>{source.ref_id}</span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {source.source_kind === "open_access" ? (
                          <a href={source.external_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-950 hover:text-sky-700 hover:underline">{source.title}</a>
                        ) : (
                          <Link to={`/items/${source.item_id}`} className="font-semibold text-slate-950 hover:text-orange-700 hover:underline">{source.title}</Link>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${source.source_kind === "open_access" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600"}`}>{source.source_kind === "open_access" ? "Open access · OpenAlex" : "Your library"}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{source.authors?.join(", ") || "Unknown author"}{source.year ? ` · ${source.year}` : ""}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 flex gap-3 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p><strong>AI-generated literature draft.</strong> Verify all claims and citations before academic submission. References are generated from The Archive's library metadata.</p>
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 text-center">
          <Sparkles className="w-6 h-6 text-orange-500 mx-auto mb-2" />
          <p className="text-slate-700 font-semibold">Created with the AI Researcher of The Archive</p>
          <p className="text-sm text-slate-500 mt-1">Generate your own evidence-grounded research drafts from thousands of scientific documents.</p>
          <Button asChild variant="outline" className="mt-4"><Link to="/research">Try the AI Researcher</Link></Button>
        </div>
      </main>
    </div>
  );
}
